import React from 'react';
import { useNavigate } from 'react-router';
import AirportThresholdsList from '../../../app/components/monitor-status/AirportThresholdsList';
import { EventBus } from '../../../app/contexts/event-bus';
import { OpenNotesCommand } from '../../../app/features/home/flyouts/Notes';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/store-hooks';
import { useFlightWorker } from '../../../app/hooks/use-flight-worker';
import { useSafeEffect } from '../../../app/hooks/use-safe-effect';
import { ROUTES } from '../../../app/router/routes';
import { toggleLeftFlyout } from '../../../app/state/slices/workspace-slice';
import useMapInteraction from '../../../hooks/use-map-interaction';
import { AirportTowerOutline, BorderColorOutline } from '../../../icons/icons';
import { MessageType } from '../../../services/actor-messages';
import { EliService } from '../../../services/external-link-interface/EliService';
import { AirportContextMenuData } from '../../../services/map-interaction-service';
import { NoteCategory } from '../../../types/notes';
import { EliCustomButton } from '../../../types/settings';
import { Menu, MenuItem, NavigatingMenuItem } from '../../common/menu/DropdownMenu';

export function AirportContextMenu(props: { info: AirportContextMenuData }) {
    const context = props.info;
    const onlyShowBreachedThresholds = useAppSelector((x) => x.settings.hideNonBreachedThresholdsAirportContextMenu);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const interaction = useMapInteraction();

    const handleAirportSummary = React.useCallback(() => {
        dispatch(toggleLeftFlyout({ type: 'airport', airportId: context.airportId }));
        interaction.dismissContextMenu();
        if (location.pathname !== ROUTES.HOME) {
            navigate(ROUTES.HOME);
        }
    }, [context.airportId]);

    const handleDiversionHolding = React.useCallback(() => {
        EventBus.fire('show-diversion', context.airportId!);
        interaction.dismissContextMenu();
    }, [context.airportId]);

    const handleNotes = React.useCallback(() => {
        if (location.pathname !== ROUTES.HOME) {
            if (context && context.airportId) {
                EventBus.fire('show-notes-window', {
                    itemId: context.airportId,
                    category: NoteCategory.airport
                } satisfies OpenNotesCommand);
            }
        } else {
            dispatch(toggleLeftFlyout({ type: 'notes', airportId: context.airportId }));
        }
        interaction.dismissContextMenu();
    }, [context.airportId]);

    return (
        <>
            <NavigatingMenuItem
                label="Airport summary"
                icon={AirportTowerOutline}
                navigateTo={ROUTES.HOME}
                onClick={handleAirportSummary}
            />
            <Menu label="Breached thresholds">
                <MenuItem>
                    <AirportThresholdsList id={context.airportId} onlyShowBreached={onlyShowBreachedThresholds} />
                </MenuItem>
            </Menu>
            <MenuItem label="Diversion/Holding status" onClick={handleDiversionHolding} />
            <MenuItem className="border-top" label="Notes" icon={BorderColorOutline} onClick={handleNotes} />
            <AirportEli airport={context.airportId} />
        </>
    );
}

export function AirportEli({ airport }: { airport: string }) {
    const eli = useAppSelector((x) => x.settings.eliAirportButtons);
    const worker = useFlightWorker();
    const [filtered, setFiltered] = React.useState<EliCustomButton[]>([]);
    const handleOnClick = (eli: EliCustomButton) => {
        EliService.handleAirport(eli, airport);
    };

    useSafeEffect(() => {
        const handler = async () => {
            const result: EliCustomButton[] = [];
            for (const item of eli) {
                if (!item.filter) {
                    result.push(item);
                } else if (
                    await worker.actor.sendAsync({
                        type: MessageType.validatePattern,
                        data: {
                            pattern: item.filter,
                            text: airport,
                            isAirport: true
                        }
                    })
                ) {
                    result.push(item);
                }
            }
            setFiltered(result);
        };
        handler();
    }, [worker, eli, airport]);

    if (filtered.length >= 3) {
        return (
            <Menu label="External Link Interface" className="border-top">
                {filtered.map((x, i) => (
                    <MenuItem key={i} label={x.label} onClick={() => handleOnClick(x)}></MenuItem>
                ))}
            </Menu>
        );
    }
    return (
        <>
            <div className="border-top"></div>
            {filtered.map((x, i) => (
                <MenuItem key={i} label={x.label} onClick={() => handleOnClick(x)}></MenuItem>
            ))}
        </>
    );
}
